import { Header } from '@/components/Header';
import { PhoneCallBar } from '@/components/PhoneCallBar';
import { selectPartnerNumber } from '@/context/display';
import { useAppSelector } from '@/context/storeHooks';
import { PHONE } from '@/utils/constants';

/**
 * The holding screen shown when a partner's widget is locked.
 *
 * A widget goes live the moment a partner installs the embed, so a brand can
 * be handed over before its setup fee is paid. `widgetLocked` on the brand
 * holds the journey closed: the embed still loads, but nothing starts and
 * the applicant gets this instead, with a number they can ring.
 *
 * **Nothing in here is partner-specific.** The accent is `--primaryColor`,
 * written by `setCssVariables` from the partner payload at bootstrap, which
 * falls back to Apply's own `#f24a4b` from `global.css` for a partner that
 * sets no colour. The number is `selectPartnerNumber`, the same source the
 * referred, declined and technical-failure screens use. The logo and Log in
 * come from the shared Header. No brand is named in the copy — "our finance
 * team" is true for every partner.
 *
 * Built as a plain flex column rather than with `DisplayComponent`, which
 * reports a screen view on mount. That would be a brand-new tracked event,
 * and old sessions would never carry it, so the funnel would be comparing
 * unlike periods. A holding screen needs no tracking change to work: nobody
 * reaching it has entered the journey.
 */

export const testId = 'WidgetLocked';

export function WidgetLocked(): JSX.Element {
    // Falls back the way the other terminal screens do, so a partner with no
    // number configured still shows something dialable rather than a blank bar.
    const phone = useAppSelector(selectPartnerNumber) || PHONE;

    return (
        // h-full, and the story/app must give it a parent with a real height.
        // The call bar is pinned to the bottom by the grown spacer below, so
        // an ancestor with no height collapses the whole screen to its content.
        <div
            data-testid={testId}
            className='_flex h-full w-[394px] flex-col bg-white px-5'
        >
            {/* -mx-5 so the rule spans the full width while the content it
                separates stays inside the page padding. */}
            <div className='-mx-5 border-b border-cardEdge px-5'>
                <Header />
            </div>

            {/* Status pill. Says the state in two words, before the headline. */}
            <span className='mt-8 inline-flex w-fit animate-rise items-center gap-2 rounded-full bg-primaryTint px-3 py-1.5 text-[13px] font-semibold text-primary [animation-delay:.1s] motion-reduce:animate-none'>
                {/* A halo leaving the dot, mixed from the partner's colour
                    rather than drawn in a fixed red. The dot itself stays
                    put, so it still reads as a status marker when the
                    animation is off. */}
                <span className='h-2 w-2 shrink-0 animate-live-pulse rounded-full bg-primary motion-reduce:animate-none' />
                Coming soon
            </span>

            <h1 className='mt-5 animate-rise text-[32px] font-extrabold leading-[1.12] tracking-tight text-panel [animation-delay:.22s] motion-reduce:animate-none'>
                Online finance is almost here.
            </h1>

            <p className='mt-4 animate-rise text-[16px] leading-relaxed text-textBody [animation-delay:.34s] motion-reduce:animate-none'>
                We&apos;re putting the finishing touches on applying online.
                Until then, our finance team can get you started over the
                phone.
            </p>

            {/* Holds the call bar on the bottom edge at any embed height,
                rather than letting it ride up under the paragraph. */}
            <div className='flex-1' />

            {/* Bar and caption rise together, last: they belong to each
                other, and the caption landing on its own would read as an
                afterthought rather than a footnote to the bar. */}
            <div className='animate-rise [animation-delay:.5s] motion-reduce:animate-none'>
                {/* The only action on the screen, and a shared component
                    rather than markup local to this file — see PhoneCallBar.
                    No baseId: a new click event is a tracking change, which
                    this screen deliberately does not make. */}
                <PhoneCallBar label='Speak to our finance team' phone={phone} />

                {/* Tells them the page itself becomes the application, so
                    they know to come back here rather than go looking. */}
                <p className='pb-1 pt-3 text-center text-xs text-textMuted'>
                    Online applications will open on this page.
                </p>
            </div>
        </div>
    );
}
